<template>
  <section
    v-if="images.length"
    ref="galleryRef"
    class="gallery w-full overflow-hidden bg-white md:rounded lg:flex lg:gap-4 lg:py-5 lg:px-10"
    :style="{ ...(!!galleyHeight && { height: `${galleyHeight}px` }) }"
  >
    <client-only>
      <div :id="galleryID" class="main-image relative">
        <div class="flex justify-center items-center">
          <swiper-container
            ref="mainImageRef"
            v-bind="MAIN_IMAGE_SWIPER_OPTIONS"
            @swiperafterinit="handleMainImageSwiperInit"
            @swiperslidechange="handleMainImageSwiperSlideChange"
          >
            <swiper-slide v-for="(img, index) in images" :key="img" class="cursor-pointer !w-full" @click="handleClickMainCarouselImage">
              <etg-img
                :src="`fn:${img}`"
                :alt="branch?.name"
                class="w-full aspect-1 object-cover bg-gray-30 md:rounded"
                :fetchpriority="index === 0 ? 'high' : undefined"
              />
            </swiper-slide>
          </swiper-container>
        </div>
        <div v-if="!appStore.isLargeDesktopScreen" class="overlay absolute top-0 left-0 w-full h-full z-1 pointer-events-none">
          <!-- <favorite-button
            v-if="appStore.isMobileScreen"
            :id="branch.pid!"
            class="absolute top-2 right-2 pointer-events-auto"
            :is-favorited="branch?.is_favorite || false"
          /> -->
          <div class="absolute bottom-3 right-5 bg-black-40 text-white px-2 py-0.5 text-sm rounded">
            <span>{{ activeIndex + 1 }} / {{ images.length }}</span>
          </div>
        </div>
      </div>

      <div v-if="appStore.isLargeDesktopScreen" class="aside-carousel relative h-full">
        <swiper-container
          ref="asideCarouselRef"
          v-bind="ASIDE_CAROUSEL_SWIPER_OPTIONS"
          @swiperafterinit="handleAsideCarouselSwiperInit"
          @swiperslidechange="handleAsideCarouselSwiperSlideChange"
        >
          <swiper-slide v-for="(img, idx) in images" :key="img" class="lg:size-50px xl:size-92px cursor-pointer">
            <etg-img :src="`fn:${img}`" class="aspect-1 object-cover bg-gray-30 rounded" @click="handleClickAsideCarouselImage(idx)" />
          </swiper-slide>
        </swiper-container>
        <button :id="prevBtnId" class="swiper-button-prev absolute top-0 left-0 w-full h-22px flex justify-center z-1 transition-opacity">
          <svg-icon name="arrow-up" class="w-8 h-8 p-1.5 -mt-1.5" />
        </button>
        <button :id="nextBtnId" class="swiper-button-next absolute bottom-0 left-0 w-full h-22px flex justify-center z-1 transition-opacity">
          <svg-icon name="arrow-down" class="w-8 h-8 p-1.5 -mt-1.5" />
        </button>
      </div>

      <bottom-drawer
        v-if="!appStore.isLargeDesktopScreen"
        v-model:visible="isGalleryDrawerVisible"
        :is-footer-shown="false"
        :with-trigger-btn="false"
        @after-enter="handelMobileDrawerOpen"
      >
        <div class="mobile-gallery-drawer-root flex flex-wrap content-start p-3 gap-2">
          <div
            v-for="(img, idx) in images"
            :id="`${mobileGalleryImageIdPrefix}${idx}`"
            :key="img"
            class="cursor-pointer"
            :class="[(idx + 1) % 3 ? 'w-[calc(50%-4px)]' : 'w-full']"
            @click="handleMobileGalleryImageClicked(idx)"
          >
            <etg-img :src="`fn:${img}`" :alt="branch?.name" class="w-full aspect-1 object-cover bg-gray-30" />
          </div>
        </div>
      </bottom-drawer>

      <template #fallback>
        <n-skeleton class="aspect-1 rounded h-full w-full lg:w-[calc(100%-50px-16px)] xl:w-[calc(100%-92px-16px)]" />
      </template>
    </client-only>
  </section>
</template>

<script setup lang="ts">
import type { Swiper } from 'swiper'
import { NSkeleton } from 'naive-ui'

import BottomDrawer from '@/components/common/bottom-drawer.vue'

import { useAppStore } from '@/store/app'

// import FavoriteButton from '@/components/common/favorite-button.vue'

const galleryID = 'product-gallery-main-image'
const mobileGalleryImageIdPrefix = 'mobile-gallery-image_'
const prevBtnId = `prev-btn-${getRandomString()}`
const nextBtnId = `next-btn-${getRandomString()}`

const { init: lightboxInit, open: lightboxOpen } = useLightbox(galleryID)

const appStore = useAppStore()

const { branch } = useBranchDetailContext()

const images = computed(() => branch.value?.images || [])

const isGalleryDrawerVisible = ref(false)
const activeIndex = ref(0)

const galleryRef = ref<HTMLDivElement>()
const mainImageRef = ref<HTMLDivElement>()
const asideCarouselRef = ref<HTMLDivElement>()

const mainImageSwiper = ref<Swiper>()

const gallerySize = useElementSize(galleryRef)

const galleyHeight = computed(() => {
  if (gallerySize.width.value === 0) {
    return null
  }
  if (!appStore.isLargeDesktopScreen) {
    return gallerySize.width.value
  }

  const gap = 16
  const asideWidth = appStore.isExtraLargeDesktop ? 92 : 50
  const paddingY = 20 * 2

  return gallerySize.width.value - gap - asideWidth + paddingY
})

const MAIN_IMAGE_SWIPER_OPTIONS = {
  slidesPerView: 1,
  spaceBetween: 16,
  pagination: false,
}
const handleMainImageSwiperInit = (e: CustomEvent<[Swiper]>) => {
  const swiperInstance = e.detail[0]
  mainImageSwiper.value = swiperInstance

  lightboxInit()
}
const handleMainImageSwiperSlideChange = () => {
  if (!mainImageSwiper.value) return

  syncIndex(mainImageSwiper.value.activeIndex)
}

const asideCarouselSwiper = ref<Swiper>()
const ASIDE_CAROUSEL_SWIPER_OPTIONS = {
  slidesPerView: 'auto',
  slidesPerGroup: 1,
  spaceBetween: 16,
  direction: 'vertical',
  navigation: {
    nextEl: `#${nextBtnId}`,
    prevEl: `#${prevBtnId}`,
    disabledClass: `opacity-0`,
  },
}
const handleAsideCarouselSwiperInit = (e: CustomEvent<[Swiper]>) => {
  const swiperInstance = e.detail[0]
  asideCarouselSwiper.value = swiperInstance
}
const handleAsideCarouselSwiperSlideChange = () => {
  if (!asideCarouselSwiper.value) return

  syncIndex(asideCarouselSwiper.value.activeIndex)
}
const handleClickAsideCarouselImage = (idx: number) => {
  if (!asideCarouselSwiper.value) return

  syncIndex(idx)
}

const handleClickMainCarouselImage = (e: Event) => {
  if (!mainImageSwiper.value) return
  if (!appStore.isLargeDesktopScreen) {
    e.stopPropagation()
    openMobileGalleryDrawer()
  }
}

const handleMobileGalleryImageClicked = (idx: number) => {
  lightboxOpen(idx)
  syncIndex(idx)
}

const openMobileGalleryDrawer = () => {
  if (!mainImageSwiper.value) return
  isGalleryDrawerVisible.value = true
}

const handelMobileDrawerOpen = () => {
  scrollToId(`${mobileGalleryImageIdPrefix}${activeIndex.value}`, true)
}

const syncIndex = (idx: number) => {
  activeIndex.value = idx

  if (mainImageSwiper.value) mainImageSwiper.value.slideTo(idx)
  if (asideCarouselSwiper.value) asideCarouselSwiper.value.slideTo(idx)
}

useResizeObserver(mainImageRef, entries => {
  if (asideCarouselRef.value) {
    const [entry] = entries
    const { height } = entry.contentRect
    asideCarouselRef.value.style.height = `${height}px`
  }
})
</script>

<style lang="scss" scoped>
.gallery {
  .swiper-button-prev {
    background: linear-gradient(0, rgba(255, 255, 255, 0) 0%, #fff 100%);
  }
  .swiper-button-next {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 100%);
  }
}
.mobile-gallery-drawer-root {
  height: var(--drawer-body-max-height);
}
</style>
